import { cloneData, cloneDataOrNull, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../../../dto/dto.js';
import { MapUtils } from '../../../../utility/map-utils.js';
import { LegacyPassCartItemDto } from './legacy-pass-cart-item.js';
import { OpenAccessPassTemplate } from '../../open-access-pass/open-access-pass-template.js';
import { CustomerProfile } from '../../customer-profile.js';
import { OpenAccessPassVariation } from '../../open-access-pass/open-access-pass-variation.js';
import { ComponentList } from '../../../../components/core/component-list.js';
import { VersionRange } from '../../../core/version-range.js';

type LegacyOpenAccessPassCartItemDtoCreateBaseArgs = {
  passId: string,
  viaProductId: string | null,
  viaGroupId: string | null,
  customer: CustomerProfile | null,
  template: OpenAccessPassTemplate,
  variation: OpenAccessPassVariation | null,
  components?: ComponentList,
};

type LegacyOpenAccessPassCartItemDtoCreateWithCodeArgs = LegacyOpenAccessPassCartItemDtoCreateBaseArgs & {
  code: string,
};

type LegacyOpenAccessPassCartItemDtoCreateWithScanCodeArgs = LegacyOpenAccessPassCartItemDtoCreateBaseArgs & {
  scanCode: string,
};

export class LegacyOpenAccessPassCartItemDto extends LegacyPassCartItemDto {
  static readonly TYPE_ID: string = '10d7eba3031e4557afcdf1448d6adf08';
  static readonly SCHEMA_VERSION: number = 5;

  private constructor(
      public readonly passId: string,
      public readonly code: string,
      public readonly viaProductId: string | null,
      public readonly viaGroupId: string | null,
      public readonly customer: CustomerProfile | null,
      public readonly template: OpenAccessPassTemplate,
      public readonly variation: OpenAccessPassVariation | null,
      public readonly components: ComponentList,
  ) {
    super();
  }

  get $typeId(): string {
    return LegacyOpenAccessPassCartItemDto.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    // Version should always be `exactly`.
    // Cart should never be backwards migrated, backend should be updated before frontend.
    return VersionRange.exactly(LegacyOpenAccessPassCartItemDto.SCHEMA_VERSION);
  }

  static create(args: LegacyOpenAccessPassCartItemDtoCreateWithCodeArgs): LegacyOpenAccessPassCartItemDto;
  static create(args: LegacyOpenAccessPassCartItemDtoCreateWithScanCodeArgs): LegacyOpenAccessPassCartItemDto;
  static create(args: LegacyOpenAccessPassCartItemDtoCreateWithCodeArgs | LegacyOpenAccessPassCartItemDtoCreateWithScanCodeArgs): LegacyOpenAccessPassCartItemDto {
    const code = 'code' in args ? args.code : args.scanCode;
    return new LegacyOpenAccessPassCartItemDto(
        args.passId,
        code,
        args.viaProductId,
        args.viaGroupId,
        args.customer,
        args.template,
        args.variation,
        args.components ?? new ComponentList(),
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): LegacyOpenAccessPassCartItemDto {
    return new LegacyOpenAccessPassCartItemDto(
        MapUtils.getString(map, 'passId'),
        MapUtils.getString(map, 'code'),
        MapUtils.getStringOrNull(map, 'viaProductId'),
        MapUtils.getStringOrNull(map, 'viaGroupId'),
        context.fromDataOrNull(MapUtils.getRecord(map, 'customer')),
        context.fromData(MapUtils.getRecord(map, 'template')),
        context.fromDataOrNull(MapUtils.getRecordOrNull(map, 'variation')),
        ComponentList.deserialize(context, MapUtils.get<SerializedData[]>(map, 'components')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'passId': this.passId,
      'code': this.code,
      'scanCode': this.code,
      'viaProductId': this.viaProductId,
      'viaGroupId': this.viaGroupId,
      'customer': context.toDataOrNull(this.customer),
      'template': context.toData(this.template),
      'variation': context.toDataOrNull(this.variation),
      'components': this.components.serialize(context),
    };
  }

  clone(): this {
    return new LegacyOpenAccessPassCartItemDto(
        this.passId,
        this.code,
        this.viaProductId,
        this.viaGroupId,
        cloneDataOrNull(this.customer),
        cloneData(this.template),
        cloneDataOrNull(this.variation),
        this.components.clone(),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.template, this.template) &&
      isSameData(other.variation, this.variation) &&
      other.passId === this.passId &&
      other.code === this.code &&
      other.viaProductId === this.viaProductId &&
      other.viaGroupId === this.viaGroupId &&
      isSameData(other.customer, this.customer) &&
      isSameData(other.components, this.components);
  }

  static assertType(item: LegacyPassCartItemDto): item is LegacyOpenAccessPassCartItemDto {
    return item instanceof LegacyOpenAccessPassCartItemDto;
  }

  get scanCode(): string {
    return this.code;
  }
}

export class LegacyOpenAccessPassCartItemDtoBuilder extends DataModelBuilder<LegacyOpenAccessPassCartItemDto> {
  build(context: SerializationContext, map: SerializedData): LegacyOpenAccessPassCartItemDto {
    return LegacyOpenAccessPassCartItemDto.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(LegacyOpenAccessPassCartItemDto.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(LegacyOpenAccessPassCartItemDto.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version parameter `components` of type `ComponentList` was introduced.
    if(version < 2) {
      version = 2;
      map['components'] = [];
    }

    // In this version parameter `scanCode` was renamed to `code`.
    if(version < 3) {
      map['code'] = MapUtils.getString(map, 'scanCode');
      version = 3;
    }

    // In this version parameter `viaProductId` of type `string | null` was added.
    if(version < 4) version = 4;

    // In this version parameter `viaGroupId` of type `string | null` was added.
    if(version < 5) version = 5;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
